import RaidPlan, {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import { getEncounterTimelineData } from 'app/frontend/base/services/raidPlanData'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { resolvePhaseTiming } from '../../TeamPlans/utils/phaseTiming'
import { resolveEncounterAssignmentRows } from '../../TeamPlans/utils/encounterAssignmentRows'

export type LinkedPlanAssignment = {
  abilityKey: string
  ability: RaidPlanEncounterAssignmentAbility
  row: EncounterAbilityRow
}

export const buildLinkedPlanAssignments = ({
  encounterAssignments,
  encounterRows,
}: {
  encounterAssignments: RaidPlanTimeline['encounter_assignments']
  encounterRows: EncounterAbilityRow[]
}): LinkedPlanAssignment[] => {
  const abilitiesByKey = encounterAssignments?.abilities
  const abilityOrder = encounterAssignments?.ability_order
  const rowByAbilityKey = encounterRows.reduce<
    Record<string, EncounterAbilityRow>
  >((acc, row) => {
    acc[row.abilityKey] = row
    return acc
  }, {})
  const toAssignment = (
    abilityKey: string,
    ability?: RaidPlanEncounterAssignmentAbility,
  ): LinkedPlanAssignment[] => {
    const row = rowByAbilityKey[abilityKey]
    if (!ability || !row) return []
    return [{ abilityKey, ability, row }]
  }
  const orderedAssignments = (abilityOrder ?? []).flatMap((abilityKey) =>
    toAssignment(abilityKey, abilitiesByKey?.[abilityKey]),
  )
  const orderedKeySet = new Set(
    orderedAssignments.map((entry) => entry.abilityKey),
  )
  const missingAssignments = Object.entries(abilitiesByKey ?? {})
    .filter(([abilityKey]) => !orderedKeySet.has(abilityKey))
    .flatMap(([abilityKey, ability]) => toAssignment(abilityKey, ability))
  return [...orderedAssignments, ...missingAssignments]
}

type ResolveLinkedPlanEncounterAssignmentRowsOptions = {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
  unknownAbilityLabel: string
}

type LinkedPlanPhaseDivider = {
  name: string
  at: number
}

const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30

export const resolveLinkedPlanTimelineMaxTime = ({
  raidPlan,
  timeline,
}: {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
}) => {
  const encounterTimeline = raidPlan
    ? getEncounterTimelineData()[raidPlan.encounterId.toString()]
    : undefined
  const encounterDifficulty =
    raidPlan && encounterTimeline
      ? encounterTimeline.difficulties.find(
          (difficulty) => difficulty.difficulty === raidPlan.difficulty,
        ) ?? encounterTimeline.difficulties[0]
      : undefined

  const encounterMax = encounterDifficulty?.maxTime ?? 0
  if (encounterMax > 0) {
    return roundUpToThirtySeconds(Math.max(60, encounterMax))
  }

  const phaseDurationsMax =
    encounterDifficulty?.phases.reduce(
      (sum, phase) => sum + phase.duration,
      0,
    ) ?? 0
  const actorMax = timeline.actors.reduce((max, actor) => {
    return actor.casts.reduce((innerMax, cast) => {
      if (cast.spell_id === null) return innerMax
      return Math.max(innerMax, cast.at)
    }, max)
  }, 0)

  return roundUpToThirtySeconds(Math.max(60, phaseDurationsMax, actorMax))
}

export const resolveLinkedPlanPhaseDividers = ({
  raidPlan,
  timeline,
}: {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
}): LinkedPlanPhaseDivider[] => {
  if (!raidPlan) return []
  const encounterTimeline =
    getEncounterTimelineData()[raidPlan.encounterId.toString()]
  if (!encounterTimeline) return []
  const encounterDifficulty =
    encounterTimeline.difficulties.find(
      (difficulty) => difficulty.difficulty === raidPlan.difficulty,
    ) ?? encounterTimeline.difficulties[0]
  if (!encounterDifficulty) return []
  const { phaseEnds } = resolvePhaseTiming({
    phases: encounterDifficulty.phases,
    persistedPhaseDurations:
      timeline.phase_durations ?? raidPlan.timeline?.phase_durations,
  })
  return encounterDifficulty.phases.map((phase, index) => ({
    name: phase.name,
    at: phaseEnds[index] ?? 0,
  }))
}

export const resolveLinkedPlanEncounterAssignmentRows = ({
  raidPlan,
  timeline,
  unknownAbilityLabel,
}: ResolveLinkedPlanEncounterAssignmentRowsOptions): EncounterAbilityRow[] => {
  if (!raidPlan) return []
  const encounterTimeline =
    getEncounterTimelineData()[raidPlan.encounterId.toString()]
  if (!encounterTimeline) return []
  const encounterDifficulty =
    encounterTimeline.difficulties.find(
      (difficulty) => difficulty.difficulty === raidPlan.difficulty,
    ) ?? encounterTimeline.difficulties[0]
  if (!encounterDifficulty) return []

  const {
    defaultPhaseStarts,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
  } = resolvePhaseTiming({
    phases: encounterDifficulty.phases,
    persistedPhaseDurations:
      timeline.phase_durations ?? raidPlan.timeline?.phase_durations,
  })

  return resolveEncounterAssignmentRows({
    encounterDifficulty,
    defaultPhaseStarts,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
    unknownAbilityLabel,
    abilityOverridesByKey: timeline.encounter_assignments?.abilities,
  })
}
